/* eslint-disable @typescript-eslint/no-magic-numbers */
import { defaultLayerThickness, getLayerIdentifier, getOrderedCopperLayers, isCopperLayer, isDeprecatedPcbNodeType, LayoutRules, matchLayerIdentifier, PcbLayoutFootprintPadShape, PcbNodeTypes, TraceShapeType, } from "@buildwithflux/models";
import { clampBetween, enforceGridPrecisionInMeters } from "@buildwithflux/shared";
import { useGLTF } from "@react-three/drei";
import { getAncestorUidByType, PcbNodeHelper } from "../../../nodes";
import { packages } from "../../../parametric_footprints/packages";
import { getProcessed3dModelUrl } from "../../../part";
import { FootPrintPadHoleType, LayerOrientation, PcbLayoutShape, PcbViaThermalReliefConductorAngle, PcbViaType, } from "../../../pcb/rules";
import { getEnrichedViaOptions, getSelectedViaConfigs } from "../../../smart_via";
import { getAncestorElementData, getAssetByName, getSubstrateThickness } from "../helpers";
import { computeEffectiveNetForNode } from "../helpers/computeEffectiveNetForNode";
import { isValueMetric } from "../helpers/isValueMetric";
import { getStackupLayersFromValue, getStackupViaHoleConfigsFromValue, getStackupViasFromValue, RuleParser, } from "../rule_parsing_and_compilation/PcbLayoutRuleParser";
import { getExistingPadNodes } from "../virtual_node_generators/generators/footprint/helpers/getExistingPadNodes";
import { circleNodeRuleKeys, containerNodeRuleKeys, elementNodeRuleKeys, fillNodeRuleKeys, footprintNodeRuleKeys, groupNodeRuleKeys, layoutNodeRuleKeys, lineNodeRuleKeys, modelNodeRuleKeys, netNodeRuleKeys, padNodeRuleKeys, polygonNodeRuleKeys, rectangleNodeRuleKeys, rootNodeRuleKeys, routeNodeRuleKeys, smartViaNodeRuleKeys, textNodeRuleKeys, viaNodeRuleKeys, zoneNodeRuleKeys, } from "./PcbNodeRuleKeys";
export class PcbNodeBakedRulesDeriver {
    logger;
    treeManager;
    computeEffectiveNetForNodeCache = { allNetNodesCache: new Map() };
    constructor(treeManager, logger) {
        this.logger = logger;
        this.treeManager = treeManager;
    }
    /**
     * TODO: Ideally, we would love to see this function to have signature like
     *
     * getBakedRulesForNode<T extends PcbNodeTypes>(
     nodeData: DeepReadonly<PcbNode<T>>,
     ...
     * ): BakedRulesFor<T> {...}
     * so that the returned value is binded to the node type.
     *
     * In other words, we want this function to be more generic to the caller, so caller
     * is assured to get a certain result for a given node type.
     * I think this is a next step until we have more setup done in upstream, see related comment:
     * https://github.com/buildwithflux/flux-app/pull/3101#discussion_r1017576550
     *
     * When returning undefined, it signals the node should be skipped from baking
     */
    getBakedRulesForNode(nodeData, flattenedRules, documentState, expressionEvaluationContext) {
        // Side note: most of the rules computed below include position/rotation/layer rules. For detail on
        // how these rules should play together in the renderer, read the explanation doc here:
        // https://coda.io/d/Engineering-Design-Documents_d3JGWo5rzfR/Position-and-orientation-in-PCB-mode_suyzB#_luHXc
        if (isDeprecatedPcbNodeType(nodeData.type)) {
            return undefined;
        }
        switch (nodeData.type) {
            case PcbNodeTypes.layout:
                return this.computeLayoutNodeRules(flattenedRules, nodeData, expressionEvaluationContext);
            case PcbNodeTypes.container:
                return this.computeContainerNodeRules(flattenedRules, nodeData, expressionEvaluationContext);
            case PcbNodeTypes.group:
                return this.computeGroupNodeRules(flattenedRules, nodeData, expressionEvaluationContext);
            case PcbNodeTypes.fill:
                return this.computeFillNodeRules(flattenedRules, nodeData, expressionEvaluationContext);
            case PcbNodeTypes.text:
                return this.computeTextShapeNodeRules(flattenedRules, nodeData, expressionEvaluationContext);
            case PcbNodeTypes.circle:
                return this.computeCircleShapeNodeRules(flattenedRules, nodeData, expressionEvaluationContext);
            case PcbNodeTypes.rectangle:
                return this.computeRectangleShapeNodeRules(flattenedRules, nodeData, expressionEvaluationContext);
            case PcbNodeTypes.line:
                return this.computeLineShapeNodeRules(flattenedRules, nodeData, expressionEvaluationContext);
            case PcbNodeTypes.pad:
                return this.computePadNodeRules(flattenedRules, nodeData, expressionEvaluationContext);
            case PcbNodeTypes.via:
                return this.computeViaNodeRules(flattenedRules, nodeData, expressionEvaluationContext, documentState);
            case PcbNodeTypes.smartVia:
                return this.computeSmartViaNodeRules(flattenedRules, nodeData, expressionEvaluationContext);
            case PcbNodeTypes.net:
                return this.computeNetNodeRules(flattenedRules, nodeData, expressionEvaluationContext);
            case PcbNodeTypes.routeSegment:
                return this.computeRouteSegmentNodeRules(flattenedRules, nodeData, expressionEvaluationContext, documentState);
            case PcbNodeTypes.element:
                return this.computeElementNodeRules(flattenedRules, nodeData, expressionEvaluationContext);
            case PcbNodeTypes.model:
                if (!documentState.elements || !documentState.assets) {
                    throw new Error("computeModelNodeRules requires documentState.elements and documentState.assets to be set");
                }
                return this.computeModelNodeRules(flattenedRules, nodeData, 
                // TODO Figure out a way to not require the .elements and .assets fields here
                documentState.elements, documentState.assets, expressionEvaluationContext);
            case PcbNodeTypes.footprint:
                return this.computeFootprintNodeRules(flattenedRules, nodeData, expressionEvaluationContext);
            case PcbNodeTypes.root:
                return this.computeRootNodeRules(flattenedRules, nodeData, expressionEvaluationContext);
            case PcbNodeTypes.zone:
                return this.computeZoneNodeRules(flattenedRules, nodeData, expressionEvaluationContext);
            case PcbNodeTypes.polygon:
                return this.computePolygonNodeRules(flattenedRules, nodeData, expressionEvaluationContext);
            default:
                // Treat unknown nodes the same as deprecated nodes. This should
                // never happen, but handling it means things are type-safe and
                // predictable if it does happen.
                const error = new Error(`Trying to bake a node type we don't support: ${nodeData["type"]}`);
                if ("captureError" in this.logger) {
                    this.logger.captureError(error);
                }
                else {
                    this.logger.error(error);
                }
                return undefined;
        }
    }
    computePadNodeRules(subjectLayoutRules, node, expressionEvaluationContext) {
        const parser = new RuleParser(subjectLayoutRules, expressionEvaluationContext, node.type);
        const parsed = parser.parseRules(padNodeRuleKeys);
        const parentInfo = this.getRootParentNodeUidWithLayerZ(node.uid, parsed.layer);
        const thermalRelief = this.getThermalReliefRules(subjectLayoutRules, node, expressionEvaluationContext);
        const hole = this.getPadHole(subjectLayoutRules, node, parsed.size, expressionEvaluationContext);
        const hasBottomLayers = hole?.holeType === FootPrintPadHoleType.platedThroughHole;
        const solderMaskExpansion = this.getSolderMaskLayerExpansion(subjectLayoutRules, node, parsed.size, expressionEvaluationContext);
        const { copperLayerThicknessPrimary, solderPasteLayerThicknessPrimary, solderMaskLayerThicknessPrimary, copperLayerThicknessSecondary, solderPasteLayerThicknessSecondary, solderMaskLayerThicknessSecondary, } = this.getNodeLayersThickness(parentInfo.rootFootprintOrLayoutUid, parsed.layer, hasBottomLayers);
        let cornerRadius;
        if (parsed.cornerRadius) {
            const maximumRadius = Math.min(parsed.size.x, parsed.size.y) / 2;
            cornerRadius = this.clampCornerVector4(parsed.cornerRadius, maximumRadius);
        }
        // `asset` is a special-case rule that can't be parsed the same way as simple value rules,
        // because it uses other properties from PcbRule, eg payloadAsBase64.
        let asset = subjectLayoutRules.asset;
        let size = parsed.size;
        let padShape = parsed.padShape;
        if (hole?.holeType === FootPrintPadHoleType.nonPlatedHole) {
            // Size is synced with hole size for HOLE pads, since hole size is the primary driver for pad dimension in
            // the HOLE pad case. Size usually controls annular copper dimension on non-HOLE pads. For HOLE pads, this
            // synchronisation means computing solder mask expansion for instance, can be driven by hole size as well
            // as pad size.
            size = { ...size, x: hole.holeSize.x, y: hole.holeSize.y };
            // With size sync on for HOLE pads, we want to force pad shape to be circular for non-plated holes, to ensure
            // consuming logic doesn't attempt to shape solder mask expansion based on non-circular geometry calculations
            // (ie, solder mask could be shaped in a rectangular way around a circular drill hole)
            padShape = PcbLayoutFootprintPadShape.circular;
            // Prevent HOLE pad for utilizing asset for custom pad shapes, since HOLE pads should always be circular and
            // asset would be ignored anyway
            asset = undefined;
        }
        return {
            ...parsed,
            ...parentInfo,
            size,
            padShape,
            copperLayerThicknessPrimary,
            solderPasteLayerThicknessPrimary,
            solderMaskLayerThicknessPrimary,
            copperLayerThicknessSecondary,
            solderPasteLayerThicknessSecondary,
            solderMaskLayerThicknessSecondary,
            thermalReliefType: thermalRelief.thermalReliefType,
            solderMaskExpansion,
            thermalReliefConnectConfig: thermalRelief?.thermalReliefConnectConfig,
            hole,
            cornerRadius,
            asset,
            effectiveNetId: computeEffectiveNetForNode(node, this.treeManager.treeNodes, undefined, this.computeEffectiveNetForNodeCache),
        };
    }
    computeFootprintNodeRules(subjectLayoutRules, node, expressionEvaluationContext) {
        const parser = new RuleParser(subjectLayoutRules, expressionEvaluationContext, node.type);
        const parsed = parser.parseRules(footprintNodeRuleKeys);
        const substrateThickness = getSubstrateThickness(Object.values(parsed.stackup ?? {}));
        const parentInfo = this.getRootParentNodeUid(node.uid);
        // `asset` is a special-case rule that can't be parsed the same way as simple value rules,
        // because it uses other properties from PcbRule, eg payloadAsBase64.
        const asset = subjectLayoutRules.asset;
        const parametricGenerator = parsed.package ? packages[parsed.package] : null;
        const existingPadNodes = getExistingPadNodes(node, this.treeManager.treeNodes, this.logger) || [];
        const inferredRules = parametricGenerator?.computeInferredRules(parsed, existingPadNodes) ?? {};
        return {
            ...parentInfo,
            ...parsed,
            stackupName: subjectLayoutRules.stackup?.value?.toString(),
            substrateThickness,
            asset,
            ...inferredRules,
        };
    }
    computeLayoutNodeRules(subjectLayoutRules, node, expressionEvaluationContext) {
        const parentInfo = this.getRootParentNodeUid(node.uid);
        const parser = new RuleParser(subjectLayoutRules, expressionEvaluationContext, node.type);
        const parsed = parser.parseRules(layoutNodeRuleKeys);
        const substrateThickness = getSubstrateThickness(Object.values(parsed.stackup ?? {}));
        let cornerRadius;
        if (parsed.cornerRadius && parsed.boardShape === PcbLayoutShape.rectangle) {
            const maximumRadius = Math.min(parsed.size.x, parsed.size.y) / 2;
            cornerRadius = this.clampCornerVector4(parsed.cornerRadius, maximumRadius);
        }
        // `asset` is a special-case rule that can't be parsed the same way as simple value rules,
        // because it uses other properties from PcbRule, eg payloadAsBase64.
        const asset = subjectLayoutRules.asset;
        // Get via configs for the layout from the stackup
        const stackupRule = subjectLayoutRules.stackup;
        let viaConfigs;
        let viaHoleConfigs;
        if (stackupRule?.value) {
            viaConfigs = getStackupViasFromValue(stackupRule.value, stackupRule);
            viaHoleConfigs = getStackupViaHoleConfigsFromValue(stackupRule.value, stackupRule);
        }
        return {
            ...parentInfo,
            ...parsed,
            stackupName: subjectLayoutRules.stackup?.value?.toString(),
            isSubLayout: !!parentInfo.topLevelLayoutUid && !parentInfo.topLevelFootprintUid,
            substrateThickness,
            cornerRadius,
            asset,
            viaConfigs,
            viaHoleConfigs,
        };
    }
    computeModelNodeRules(subjectLayoutRules, node, elements, assets, expressionEvaluationContext) {
        const parser = new RuleParser(subjectLayoutRules, expressionEvaluationContext, node.type);
        const parsed = parser.parseRules(modelNodeRuleKeys);
        let storageName = undefined;
        let url = undefined;
        if (subjectLayoutRules.asset?.value) {
            const payloadFileName = subjectLayoutRules.asset.payloadFileName;
            if (!payloadFileName) {
                // this is just here for backwards compatibility
                // QUESTION: backwards compatibility to what?
                const element = getAncestorElementData(node, this.treeManager.treeNodes, elements, this.logger);
                const assetData = getAssetByName(RuleParser.stripImportantFlag(subjectLayoutRules.asset.value.toString()).trim(), assets, element);
                storageName = assetData?.storageName;
            }
            else {
                storageName = payloadFileName;
            }
            if (storageName) {
                url = getProcessed3dModelUrl(storageName);
                useGLTF.preload(url);
            }
        }
        const parentInfo = this.getRootParentNodeUidWithLayerZ(node.uid, parsed.layer);
        // To infer the parametric footprints rules for the model we need to get the pads from the parent footprint
        const parametricGenerator = parsed.package ? packages[parsed.package] : null;
        const parentFootprint = parentInfo.topLevelFootprintUid && this.treeManager.treeNodes[parentInfo.topLevelFootprintUid];
        const existingPadNodes = (parentFootprint && getExistingPadNodes(parentFootprint, this.treeManager.treeNodes, this.logger)) || [];
        const inferredRules = parametricGenerator?.computeInferredRules(parsed, existingPadNodes) ?? {};
        return {
            ...parentInfo,
            ...parsed,
            storageName,
            url,
            ...inferredRules,
        };
    }
    computeElementNodeRules(subjectLayoutRules, node, expressionEvaluationContext) {
        const parser = new RuleParser(subjectLayoutRules, expressionEvaluationContext, node.type);
        const parsed = parser.parseRules(elementNodeRuleKeys);
        return {
            ...this.getRootParentNodeUidWithLayerZ(node.uid, parsed.layer),
            ...parsed,
        };
    }
    computeContainerNodeRules(subjectLayoutRules, node, expressionEvaluationContext) {
        const parser = new RuleParser(subjectLayoutRules, expressionEvaluationContext, node.type);
        const parsed = parser.parseRules(containerNodeRuleKeys);
        return {
            ...this.getRootParentNodeUid(node.uid),
            ...parsed,
        };
    }
    computeGroupNodeRules(subjectLayoutRules, node, expressionEvaluationContext) {
        const parser = new RuleParser(subjectLayoutRules, expressionEvaluationContext, node.type);
        const parsed = parser.parseRules(groupNodeRuleKeys);
        return {
            ...this.getRootParentNodeUid(node.uid),
            ...parsed,
        };
    }
    computeRootNodeRules(subjectLayoutRules, node, expressionEvaluationContext) {
        const parser = new RuleParser(subjectLayoutRules, expressionEvaluationContext, node.type);
        const parsed = parser.parseRules(rootNodeRuleKeys);
        const bakedRootRules = {
            ...parsed,
            // QUESTION: what's the story behind overriding gridSpacingIsMetric?
            // bryhoyt: Seems like the grid renderer could/should just directly
            // inspect bakedRules.gridSpacing same as the code below and we can
            // ditch this extra layer of indirection?
            gridSpacingIsMetric: true,
            // The root node cannot have a layer. We force it to Top to avoid transformation problems.
            // See https://linear.app/buildwithflux/issue/FLUX-6473/pcb-objects-move-unnatural-when-dragged
            layer: LayerOrientation.top,
        };
        if (subjectLayoutRules.gridSpacing?.value) {
            // QUESTION: isValueMetric is not used anywhere else... is there a reason for that?
            bakedRootRules.gridSpacingIsMetric = isValueMetric(subjectLayoutRules.gridSpacing?.value ?? subjectLayoutRules.unit?.value?.toString());
        }
        return bakedRootRules;
    }
    computeZoneNodeRules(subjectLayoutRules, node, expressionEvaluationContext) {
        const parser = new RuleParser(subjectLayoutRules, expressionEvaluationContext, node.type);
        const parsed = parser.parseRules(zoneNodeRuleKeys);
        const parentInfo = this.getRootParentNodeUidWithLayerZ(node.uid, parsed.layer);
        // `asset` is a special-case rule that can't be parsed the same way as simple value rules,
        // because it uses other properties from PcbRule, eg payloadAsBase64.
        const asset = subjectLayoutRules.asset;
        let cornerRadius;
        if (parsed.cornerRadius) {
            const maximumRadius = Math.min(parsed.size.x, parsed.size.y) / 2;
            cornerRadius = this.clampCornerVector4(parsed.cornerRadius, maximumRadius);
        }
        return {
            ...parsed,
            ...parentInfo,
            cornerRadius,
            asset,
        };
    }
    computeTextShapeNodeRules(subjectLayoutRules, node, expressionEvaluationContext) {
        const parser = new RuleParser(subjectLayoutRules, expressionEvaluationContext, node.type);
        const parsed = parser.parseRules(textNodeRuleKeys);
        const parentInfo = this.getRootParentNodeUidWithLayerZ(node.uid, parsed.layer);
        const overlayThickness = this.getNodeLayersThickness(parentInfo.rootFootprintOrLayoutUid, parsed.layer, false);
        // TODO because of how defaults are handled in RuleParser, we need to handle the content rule manually.
        // It's not very tidy, so let's fix it; just need to spend a little time planning a clean mechanism.
        const content = parsed.content || "<< undefined text >>";
        parsed.scale = this.resetZScale(parsed.scale);
        return {
            ...parentInfo,
            ...parsed,
            ...overlayThickness,
            content,
        };
    }
    computeLineShapeNodeRules(subjectLayoutRules, node, expressionEvaluationContext) {
        const parser = new RuleParser(subjectLayoutRules, expressionEvaluationContext, node.type);
        const parsed = parser.parseRules(lineNodeRuleKeys);
        const parentInfo = this.getRootParentNodeUidWithLayerZ(node.uid, parsed.layer);
        const silkShapeValues = this.getSilkShapeRules(subjectLayoutRules, node, expressionEvaluationContext);
        const overlayThickness = this.getNodeLayersThickness(parentInfo.rootFootprintOrLayoutUid, parsed.layer, false);
        // `asset` is a special-case rule that can't be parsed the same way as simple value rules,
        // because it uses other properties from PcbRule, eg payloadAsBase64.
        const asset = subjectLayoutRules.asset;
        parsed.scale = this.resetZScale(parsed.scale);
        return {
            ...parentInfo,
            ...silkShapeValues,
            ...overlayThickness,
            ...parsed,
            asset,
        };
    }
    resetZScale(scale) {
        return { ...scale, x: scale.x, y: scale.y, z: 1 };
    }
    computeCircleShapeNodeRules(subjectLayoutRules, node, expressionEvaluationContext) {
        const parser = new RuleParser(subjectLayoutRules, expressionEvaluationContext, node.type);
        const parsed = parser.parseRules(circleNodeRuleKeys);
        const parentInfo = this.getRootParentNodeUidWithLayerZ(node.uid, parsed.layer);
        const silkShapeValues = this.getSilkShapeRules(subjectLayoutRules, node, expressionEvaluationContext);
        const overlayThickness = this.getNodeLayersThickness(parentInfo.rootFootprintOrLayoutUid, parsed.layer, false);
        parsed.scale = this.resetZScale(parsed.scale);
        return {
            ...parentInfo,
            ...silkShapeValues,
            ...overlayThickness,
            ...parsed,
        };
    }
    computeRectangleShapeNodeRules(subjectLayoutRules, node, expressionEvaluationContext) {
        const parser = new RuleParser(subjectLayoutRules, expressionEvaluationContext, node.type);
        const parsed = parser.parseRules(rectangleNodeRuleKeys);
        const parentInfo = this.getRootParentNodeUidWithLayerZ(node.uid, parsed.layer);
        const silkShapeValues = this.getSilkShapeRules(subjectLayoutRules, node, expressionEvaluationContext);
        const overlayThickness = this.getNodeLayersThickness(parentInfo.rootFootprintOrLayoutUid, parsed.layer, false);
        let cornerRadius;
        if (parsed.cornerRadius) {
            const sizeX = Math.abs(parsed.startPosition.x - parsed.endPosition.x);
            const sizeY = Math.abs(parsed.startPosition.y - parsed.endPosition.y);
            const maximumRadius = Math.min(sizeX, sizeY) / 2;
            cornerRadius = this.clampCornerVector4(parsed.cornerRadius, maximumRadius);
        }
        parsed.scale = this.resetZScale(parsed.scale);
        return {
            ...parentInfo,
            ...silkShapeValues,
            ...overlayThickness,
            ...parsed,
            cornerRadius,
        };
    }
    computeViaNodeRules(subjectLayoutRules, node, expressionEvaluationContext, documentState) {
        const parser = new RuleParser(subjectLayoutRules, expressionEvaluationContext, node.type);
        const parsed = parser.parseRules(viaNodeRuleKeys);
        const parentInfo = this.getRootParentNodeUidWithLayerZ(node.uid, parsed.layer);
        const thermalRelief = this.getThermalReliefRules(subjectLayoutRules, node, expressionEvaluationContext);
        const holeSize = this.getHoleSize(subjectLayoutRules, parsed.size, false, node, expressionEvaluationContext);
        const solderMaskExpansion = this.getSolderMaskLayerExpansion(subjectLayoutRules, node, parsed.size, expressionEvaluationContext);
        let viaType;
        if (subjectLayoutRules.viaType?.value) {
            // eslint-disable-next-line
            viaType = PcbViaType[RuleParser.stripImportantFlag(subjectLayoutRules.viaType.value.toString()).trim()];
        }
        const hasBottomLayers = viaType === PcbViaType.throughHole;
        const overlayThickness = this.getNodeLayersThickness(parentInfo.rootFootprintOrLayoutUid, parsed.layer, hasBottomLayers);
        // Prevent scaling of vias along z-axis, as it makes no physical sense to do so
        parsed.scale.z = 1;
        return {
            ...parentInfo,
            ...parsed,
            thermalReliefType: thermalRelief?.thermalReliefType ?? LayoutRules.thermalRelief.default,
            ...overlayThickness,
            holeSize,
            solderMaskExpansion,
            thermalReliefConnectConfig: thermalRelief?.thermalReliefConnectConfig,
            effectiveNetId: computeEffectiveNetForNode(node, this.treeManager.treeNodes, documentState.nets, this.computeEffectiveNetForNodeCache),
        };
    }
    computeSmartViaNodeRules(subjectLayoutRules, node, expressionEvaluationContext) {
        const parser = new RuleParser(subjectLayoutRules, expressionEvaluationContext, node.type);
        const parsed = parser.parseRules(smartViaNodeRuleKeys);
        const parentInfo = this.getRootParentNodeUidWithLayerZ(node.uid, parsed.layer);
        // Get rotation information separately from the rest of the rules
        // The "rotation" rule behaves differently for a Smart Via node than the rest of the nodes
        // We use the rotation value to determine the persisted angle against which we orient the smart via children
        // We don't want to bake the rotation value, and its associated rules, into the rest of the smart via rules
        const { rotation } = parser.parseRules(["rotation"]);
        const orientation = rotation.x;
        // Get the stackup info from the static Stackups object or encoded stackup rule payload (for custom stackups)
        let stackupCopperLayers = [];
        let stackupViaConfigsMap = {};
        let stackupViaConfigsArray = [];
        const stackupKey = subjectLayoutRules.stackup?.value?.toString();
        if (stackupKey) {
            const layersMap = getStackupLayersFromValue(stackupKey, true, subjectLayoutRules.stackup);
            stackupCopperLayers = getOrderedCopperLayers(layersMap);
            stackupViaConfigsMap = getStackupViasFromValue(stackupKey, subjectLayoutRules.stackup);
            stackupViaConfigsArray = Object.values(stackupViaConfigsMap);
        }
        // Expand and store the enabled via types in the baked rules
        const viaOptions = getEnrichedViaOptions(parsed.viaOptions, stackupViaConfigsMap);
        // Expand the connected layers to include all layers if "All" is selected
        // Mapping from stackupLayers ensures that layer ids stay in correct order
        const parsedConnectedLayers = parsed.connectedLayers?.filter((layerId) => !!layerId);
        const includeAllLayers = !parsedConnectedLayers?.length || parsedConnectedLayers.includes("All");
        const connectedLayers = stackupCopperLayers
            .map(getLayerIdentifier)
            .filter((layerId) => includeAllLayers || parsedConnectedLayers?.includes(layerId));
        // Get the selected via configs based on the enabled via types and the connected layers
        // Ideally we would want to extract this value from the runtime cache, but there's too much
        // abstraction in between to do that cleanly... so we have to recompute it here ;-;
        const selectedViaConfigs = getSelectedViaConfigs(viaOptions, connectedLayers, stackupViaConfigsArray, stackupCopperLayers);
        return {
            ...parentInfo,
            ...parsed,
            effectiveNetId: computeEffectiveNetForNode(node, this.treeManager.treeNodes, undefined, this.computeEffectiveNetForNodeCache),
            connectedLayers,
            viaOptions,
            selectedViaConfigs,
            orientation,
        };
    }
    computeNetNodeRules(subjectLayoutRules, node, expressionEvaluationContext) {
        const parser = new RuleParser(subjectLayoutRules, expressionEvaluationContext, node.type);
        const parsed = parser.parseRules(netNodeRuleKeys);
        const parentInfo = this.getRootParentNodeUidWithLayerZ(node.uid, parsed.layer);
        const overlayThickness = this.getNodeLayersThickness(parentInfo.rootFootprintOrLayoutUid, parsed.layer, false);
        // HACK: This is to ensure that the projected node has the correct trace width
        // remove this once we have expressions working for pseudo nodes
        const size = parsed.size ?? { x: 0, y: 0, z: 0 };
        if (node.impedance || node.current) {
            const traceWidth = parser.parseRules(["traceWidth"]).traceWidth;
            if (traceWidth) {
                size.x = traceWidth;
            }
        }
        return {
            ...parentInfo,
            ...parsed,
            ...overlayThickness,
            size,
            // Nets also have an effectiveNetId, which links them to the host net in case of sublayouts
            // This is used mostly to compute copper fills and to compute keepouts for them
            effectiveNetId: computeEffectiveNetForNode(node, this.treeManager.treeNodes, undefined, this.computeEffectiveNetForNodeCache),
        };
    }
    computeRouteSegmentNodeRules(subjectLayoutRules, node, expressionEvaluationContext, documentState) {
        const parser = new RuleParser(subjectLayoutRules, expressionEvaluationContext, node.type);
        const parsed = parser.parseRules(routeNodeRuleKeys);
        // The following determines if route segment needs to be 'flipped' - the criteria is: if an ancestor layout is bottom orientated, that implies this
        // route segment is a descendant of a flipped sub-layout. In this case, the baked start and end positions of this route segment need to be swapped.
        // Swapping ensures the segment adheres to the sub-layouts orientation. Note, route segments are a special case that need to be handled differently
        // by flipping because segment geometry is defined by start/end position rules that are context sensitive
        // Also note, we are expecting the Layout node to have already baked by the time this descendant route is being baked, due to the top-down order of
        // baking process
        const layoutUid = getAncestorUidByType(this.treeManager.treeNodes, node.uid, PcbNodeTypes.layout);
        if (layoutUid) {
            const layoutNode = this.treeManager.treeNodes[layoutUid];
            if (layoutNode && "bakedRules" in layoutNode && layoutNode.bakedRules.layer !== LayerOrientation.top) {
                // Flip route segment by swapping x coordinates on start and end positions
                switch (parsed.layer) {
                    case "Top": {
                        parsed.layer = "Bottom";
                        break;
                    }
                    case "Bottom": {
                        parsed.layer = "Top";
                        break;
                    }
                    default: {
                        [parsed.startPosition.x, parsed.endPosition.x] = [parsed.endPosition.x, parsed.startPosition.x];
                        break;
                    }
                }
            }
        }
        const parentInfo = this.getRootParentNodeUidWithLayerZ(node.uid, parsed.layer);
        const overlayThickness = this.getNodeLayersThickness(parentInfo.rootFootprintOrLayoutUid, parsed.layer, false);
        // TODO: blindly accepting/assuming that types are correct could be dangerous. If undefined returned, an incorrect data
        // type could propagate through the system
        const { traceWidth, traceCornerRadiusMinimum } = parser.parseRules(["traceWidth", "traceCornerRadiusMinimum"]);
        // Vertex specific trace shape overrides trace shape
        parsed.traceShapeStart = parsed.traceShapeStart ?? parsed.traceShape ?? TraceShapeType.straight;
        parsed.traceShapeEnd = parsed.traceShapeEnd ?? parsed.traceShape ?? TraceShapeType.straight;
        // Vertex specific corner radius overrides corner radius when present
        parsed.traceCornerRadiusStart = parsed.traceCornerRadiusStart ?? parsed.traceCornerRadius ?? 0;
        parsed.traceCornerRadiusEnd = parsed.traceCornerRadiusEnd ?? parsed.traceCornerRadius ?? 0;
        const size = parsed.size ?? { x: traceWidth ?? 0, y: 0, z: 0 };
        // Enable this when we want to re-enable solder mask opening for traces
        // const topExpansion = (parsed.solderMaskTopExpansion ?? parsed.solderMaskExpansion) as number | undefined;
        // const bottomExpansion = (parsed.solderMaskBottomExpansion ?? parsed.solderMaskExpansion) as number | undefined;
        return {
            ...parentInfo,
            ...parsed,
            ...overlayThickness,
            size,
            effectiveNetId: computeEffectiveNetForNode(node, this.treeManager.treeNodes, documentState.nets, this.computeEffectiveNetForNodeCache),
            traceCornerRadiusMinimum,
            solderMaskExpansion: undefined,
            // Swap with this when we want to re-enable solder mask opening for traces
            // solderMaskExpansion: parsed.layer === "Top" ? topExpansion : bottomExpansion,
        };
    }
    computeFillNodeRules(subjectLayoutRules, node, expressionEvaluationContext) {
        const parser = new RuleParser(subjectLayoutRules, expressionEvaluationContext, node.type);
        const parsed = parser.parseRules(fillNodeRuleKeys);
        const parentInfo = this.getRootParentNodeUid(node.uid);
        return {
            ...parentInfo,
            ...parsed,
            effectiveNetId: computeEffectiveNetForNode(node, this.treeManager.treeNodes, undefined, this.computeEffectiveNetForNodeCache),
        };
    }
    computePolygonNodeRules(subjectLayoutRules, node, expressionEvaluationContext) {
        const parser = new RuleParser(subjectLayoutRules, expressionEvaluationContext, node.type);
        const parsed = parser.parseRules(polygonNodeRuleKeys);
        let cornerRadius;
        if (parsed.cornerRadius) {
            const maximumRadius = Math.min(parsed.size.x, parsed.size.y) / 2;
            cornerRadius = this.clampCornerVector4(parsed.cornerRadius, maximumRadius);
        }
        const parentInfo = this.getRootParentNodeUid(node.uid);
        return {
            ...parentInfo,
            ...parsed,
            effectiveNetId: computeEffectiveNetForNode(node, this.treeManager.treeNodes, undefined, this.computeEffectiveNetForNodeCache),
            cornerRadius,
        };
    }
    /**
     * This returns fields under `NodeDependency` + `layerZPosition`, they all belong
     * to `CommonBakedRules`
    // Note: this only seems to be used by getNodeInfo() but internally performs layer sort, which will be done
    // repeatedly per node. Could be noticeable performance hit in complex PCBs.
     */
    getRootParentNodeUidWithLayerZ(nodeUid, nodeLayer) {
        const parentInfo = this.getRootParentNodeUid(nodeUid);
        return {
            layerZPosition: this.getLayerPositionZ(parentInfo.rootFootprintOrLayoutUid, nodeLayer),
            ...parentInfo,
        };
    }
    getThermalReliefRules(nodeLayoutRules, 
    // TODO: [NodeType] - Think more about the typing here... here we are saying the passed in
    // `node` is unbaked, but inside the method, there is logic treating it as baked
    node, expressionEvaluationContext) {
        const parser = new RuleParser(nodeLayoutRules, expressionEvaluationContext, node.type);
        const parsed = parser.parseRules([
            "thermalRelief",
            "thermalReliefConductors",
            "thermalReliefConductorAngle",
            "thermalReliefConductorWidth",
        ]);
        const thermalReliefType = parsed.thermalRelief;
        const conductors = parsed.thermalReliefConductors;
        const conductorWidth = (parsed.thermalReliefConductorWidth ?? 0);
        const angle = Math.abs((parsed.thermalReliefConductorAngle % Math.PI) - Math.PI / 2) < 0.0001
            ? PcbViaThermalReliefConductorAngle.nintyDegree
            : PcbViaThermalReliefConductorAngle.fourtyFiveDegree;
        if (conductors && angle && conductorWidth > 0) {
            return {
                thermalReliefType,
                thermalReliefConnectConfig: {
                    conductors,
                    angle,
                    conductorWidth,
                },
            };
        }
        else {
            return {
                thermalReliefType,
            };
        }
    }
    getSilkShapeRules(nodeLayoutRules, node, expressionEvaluationContext) {
        const parser = new RuleParser(nodeLayoutRules, expressionEvaluationContext, node.type);
        const parsed = parser.parseRules(["silkColor", "strokeWidth", "strokeStyle", "strokeLength", "strokeSpacing"]);
        return {
            silkColor: parsed.silkColor,
            strokeWidth: parsed.strokeWidth,
            strokeStyle: parsed.strokeStyle,
            strokeLength: parsed.strokeLength,
            strokeSpacing: parsed.strokeSpacing,
        };
    }
    getNodeLayersThickness(rootFootprintOrLayoutUid, layer, hasBottomLayers) {
        const copperLayerThicknessPrimary = this.getCopperLayerThickness(rootFootprintOrLayoutUid, layer);
        const solderPasteLayerThicknessPrimary = this.getSolderPasteLayerThickness(rootFootprintOrLayoutUid, layer);
        const solderMaskLayerThicknessPrimary = this.getSolderMaskLayerThickness(rootFootprintOrLayoutUid, layer);
        const overlayLayerThicknessPrimary = this.getOverlayLayerThickness(rootFootprintOrLayoutUid, layer);
        let copperLayerThicknessSecondary;
        let solderPasteLayerThicknessSecondary;
        let solderMaskLayerThicknessSecondary;
        if (hasBottomLayers) {
            copperLayerThicknessSecondary = this.getCopperLayerThickness(rootFootprintOrLayoutUid, layer === LayerOrientation.top ? LayerOrientation.bottom : LayerOrientation.top);
            solderPasteLayerThicknessSecondary = this.getSolderPasteLayerThickness(rootFootprintOrLayoutUid, layer === LayerOrientation.top ? LayerOrientation.bottom : LayerOrientation.top);
            solderMaskLayerThicknessSecondary = this.getSolderMaskLayerThickness(rootFootprintOrLayoutUid, layer === LayerOrientation.top ? LayerOrientation.bottom : LayerOrientation.top);
        }
        const overlayLayerThicknessSecondary = this.getOverlayLayerThickness(rootFootprintOrLayoutUid, layer === LayerOrientation.top ? LayerOrientation.bottom : LayerOrientation.top);
        return {
            copperLayerThicknessPrimary,
            solderPasteLayerThicknessPrimary,
            solderMaskLayerThicknessPrimary,
            overlayLayerThicknessPrimary,
            copperLayerThicknessSecondary,
            solderPasteLayerThicknessSecondary,
            solderMaskLayerThicknessSecondary,
            overlayLayerThicknessSecondary,
        };
    }
    clampCornerVector4(cornerRadius, maximumRadius) {
        return {
            topLeft: clampBetween(cornerRadius.topLeft, 0, maximumRadius),
            topRight: clampBetween(cornerRadius.topRight, 0, maximumRadius),
            bottomRight: clampBetween(cornerRadius.bottomRight, 0, maximumRadius),
            bottomLeft: clampBetween(cornerRadius.bottomLeft, 0, maximumRadius),
        };
    }
    getRootParentNodeUid(nodeUid) {
        // NOTE: the following three usage of `this.treeManager.treeNodes` indicate a dependency to some other nodes on the map
        const topLevelLayoutUid = getAncestorUidByType(this.treeManager.treeNodes, nodeUid, PcbNodeTypes.layout, true);
        const topLevelFootprintUid = getAncestorUidByType(this.treeManager.treeNodes, nodeUid, PcbNodeTypes.footprint, true);
        return {
            topLevelLayoutUid,
            topLevelFootprintUid,
            rootFootprintOrLayoutUid: topLevelLayoutUid ?? topLevelFootprintUid,
        };
    }
    getPadHole(subjectLayoutRules, 
    // TODO: Think more about the typing here... here we are saying the passed in`
    // `node` is unbaked, but inside the method, there is logic treating it as baked
    padNode, size, expressionEvaluationContext) {
        // TODO: [NodeType] - question: should `node` be baked or unbaked?? We have to
        // do type cast for now because we are relying on `node?.bakedRules?...` syntax
        // to compare undefined values...
        const bakedNode = PcbNodeHelper.asBakedNode(padNode);
        let holeType = bakedNode?.bakedRules.hole?.holeType;
        if (subjectLayoutRules.padHoleType?.value) {
            const strippedValue = RuleParser.stripImportantFlag(subjectLayoutRules.padHoleType.value.toString()).trim();
            switch (strippedValue) {
                case FootPrintPadHoleType.SurfaceMountDevice:
                case FootPrintPadHoleType.platedThroughHole:
                case FootPrintPadHoleType.nonPlatedHole:
                case FootPrintPadHoleType.testPinOrCardEdgeConnector:
                    // Only assign valid hole types, don't assume string is a recognized hole type
                    holeType = strippedValue;
                    break;
            }
        }
        const holeSize = this.getHoleSize(subjectLayoutRules, size, holeType === FootPrintPadHoleType.nonPlatedHole, padNode, expressionEvaluationContext);
        let holePosition = { x: 0, y: 0 };
        if ((holeType === FootPrintPadHoleType.platedThroughHole || holeType === FootPrintPadHoleType.nonPlatedHole) &&
            subjectLayoutRules.holePosition?.value !== undefined) {
            const parser = new RuleParser(subjectLayoutRules, expressionEvaluationContext, padNode.type);
            const parsed = parser.parseRules(["holePosition"]);
            const evaluatedVector2 = parsed.holePosition;
            // TODO we should check if the hole position might be out of bounds of the pad and prevent that
            if (evaluatedVector2?.x !== undefined && evaluatedVector2?.y !== undefined) {
                holePosition = {
                    x: evaluatedVector2.x,
                    y: evaluatedVector2.y,
                };
            }
        }
        if (holeType && holeSize && holePosition) {
            if (bakedNode?.bakedRules?.hole?.holeType !== holeType ||
                bakedNode?.bakedRules?.hole?.holeSize !== holeSize ||
                bakedNode?.bakedRules?.hole?.holePosition !== holePosition) {
                return { holeType, holeSize, holePosition };
            }
            else {
                return bakedNode?.bakedRules?.hole;
            }
        }
        // QUESTION: What do we do/What does it mean if its undefined?
        return undefined;
    }
    getHoleSize(subjectLayoutRules, nodeSize, holeSizeCanExpandNodeSize, node, expressionEvaluationContext) {
        const parser = new RuleParser(subjectLayoutRules, expressionEvaluationContext, node.type);
        const parsed = parser.parseRules(["holeSize"]);
        let holeSize = parsed.holeSize;
        if (!holeSizeCanExpandNodeSize) {
            if (nodeSize && holeSize && (holeSize.x > nodeSize.x || holeSize.y > nodeSize.y)) {
                holeSize = {
                    x: nodeSize.x,
                    y: nodeSize.y,
                };
            }
        }
        return holeSize;
    }
    getLayerPositionZ(rootFootprintOrLayoutUid, nodeLayer) {
        if (!rootFootprintOrLayoutUid) {
            return 0;
        }
        // NOTE: the following usage of `this.treeManager.treeNodes` indicate a dependency to some other nodes on the map
        const node = PcbNodeHelper.asBakedNode(this.treeManager.treeNodes[rootFootprintOrLayoutUid]);
        // NOTE: The node is expected to be either footprint or layout, so
        // use corresponding type predicate here to infer type for bakedRules
        const isBakedLayoutOrFootprint = node?.type === PcbNodeTypes.layout || node?.type === PcbNodeTypes.footprint;
        const layoutBakedRules = isBakedLayoutOrFootprint ? node?.bakedRules : undefined;
        const layers = !layoutBakedRules ? [] : Object.values(layoutBakedRules.stackup ?? {});
        let zPosition = 0;
        if (nodeLayer === LayerOrientation.bottom) {
            zPosition = getSubstrateThickness(layers);
        }
        else if (!nodeLayer || nodeLayer === LayerOrientation.top) {
            zPosition = 0;
        }
        else {
            const orderedStackup = Object.values(layers).sort((a, b) => a.order - b.order);
            // Start counting from first copper layer -- That's where z=0 is.
            for (const stackupLayer of orderedStackup) {
                zPosition -= stackupLayer.thickness;
                if (isCopperLayer(stackupLayer)) {
                    break;
                }
            }
            for (const stackupLayer of orderedStackup) {
                zPosition += stackupLayer.thickness;
                if (matchLayerIdentifier(stackupLayer, nodeLayer)) {
                    break;
                }
            }
        }
        zPosition += nodeLayer === "Bottom" ? 0.00002 : -0.00002; // Prevent z-fighting with PCB surface (because copper is so thin at distant zoom)
        return -zPosition;
    }
    getSolderMaskLayerExpansion(nodeLayoutRules, node, size, expressionEvaluationContext) {
        // TODO could probably clean this up further with .combineRules
        const parser = new RuleParser(nodeLayoutRules, expressionEvaluationContext, node.type);
        const parsed = parser.parseRules([
            "size",
            "solderMaskExpansion",
            "solderMaskTopExpansion",
            "solderMaskBottomExpansion",
            "solderMaskExpansionFromTheHoleEdge",
        ]);
        let topExpansion = (parsed.solderMaskTopExpansion ?? parsed.solderMaskExpansion);
        let bottomExpansion = (parsed.solderMaskBottomExpansion ?? parsed.solderMaskExpansion);
        const expansionFromTheHoleEdge = parsed.solderMaskExpansionFromTheHoleEdge;
        // TODO: [NodeType] - question: should `node` be baked or unbaked?? We have to
        // do type cast for now because we are relying on `node?.bakedRules?...` syntax
        // to compare undefiend values...
        const bakedNode = PcbNodeHelper.asBakedNode(node);
        if (bakedNode?.bakedRules.solderMaskExpansion?.topExpansion !== topExpansion ||
            bakedNode?.bakedRules.solderMaskExpansion?.bottomExpansion !== bottomExpansion ||
            bakedNode?.bakedRules.solderMaskExpansion?.expansionFromTheHoleEdge !== expansionFromTheHoleEdge) {
            // Clamp negative solder mask expansions
            const maxSizeAxis = Math.max(size.x, size.y);
            if (topExpansion && topExpansion < 0 && topExpansion < -(maxSizeAxis / 2)) {
                topExpansion = -(maxSizeAxis / 2);
            }
            if (bottomExpansion && bottomExpansion < 0 && bottomExpansion < -(maxSizeAxis / 2)) {
                bottomExpansion = -(maxSizeAxis / 2);
            }
            return {
                topExpansion,
                bottomExpansion,
                expansionFromTheHoleEdge,
            };
        }
        else {
            return bakedNode?.bakedRules?.solderMaskExpansion;
        }
    }
    getCopperLayerThickness(rootFootprintOrLayoutUid, nodeLayer) {
        if (!rootFootprintOrLayoutUid) {
            return defaultLayerThickness;
        }
        const node = this.treeManager.treeNodes[rootFootprintOrLayoutUid];
        // NOTE: The node is expected to be either footprint or layout, so
        // use corresponding type predicate here to infer type for bakedRules
        const isBakedLayoutOrFootprint = PcbNodeHelper.isBakedNode(node) &&
            (node.type === PcbNodeTypes.layout || node.type === PcbNodeTypes.footprint);
        const layoutBakedRules = isBakedLayoutOrFootprint ? node?.bakedRules : undefined;
        const pcbLayoutNodeLayers = !layoutBakedRules
            ? []
            : Object.values(layoutBakedRules.stackup ?? {});
        const layer = pcbLayoutNodeLayers.find((layer) => (layer.uid === nodeLayer || layer.orientation === nodeLayer) && isCopperLayer(layer));
        return enforceGridPrecisionInMeters(layer?.thickness ?? defaultLayerThickness);
    }
    getSolderPasteLayerThickness(rootFootprintOrLayoutUid, nodeLayer) {
        return this.getLayerThickness(rootFootprintOrLayoutUid, nodeLayer, "Solder Paste");
    }
    getSolderMaskLayerThickness(rootFootprintOrLayoutUid, nodeLayer) {
        return this.getLayerThickness(rootFootprintOrLayoutUid, nodeLayer, "Solder Mask");
    }
    getOverlayLayerThickness(rootFootprintOrLayoutUid, nodeLayer) {
        return this.getLayerThickness(rootFootprintOrLayoutUid, nodeLayer, "Overlay");
    }
    getLayerThickness(rootFootprintOrLayoutUid, nodeLayer, layerType) {
        if (!rootFootprintOrLayoutUid) {
            return defaultLayerThickness;
        }
        // NOTE: the following usage of `this.treeManager.treeNodes` indicate a dependency to some other nodes on the map
        const node = this.treeManager.treeNodes[rootFootprintOrLayoutUid];
        // NOTE: The node is expected to be either footprint or layout, so
        // use corresponding type predicate here to infer type for bakedRules
        const isBakedLayoutOrFootprint = PcbNodeHelper.isBakedNode(node) &&
            (node.type === PcbNodeTypes.layout || node.type === PcbNodeTypes.footprint);
        const layoutBakedRules = isBakedLayoutOrFootprint ? node?.bakedRules : undefined;
        const pcbLayoutNodeLayers = !layoutBakedRules
            ? []
            : Object.values(layoutBakedRules.stackup ?? {});
        const layer = pcbLayoutNodeLayers.find((layer) => layer.orientation === nodeLayer && layer.type === layerType);
        return enforceGridPrecisionInMeters(layer?.thickness ?? defaultLayerThickness);
    }
}
//# sourceMappingURL=PcbNodeBakedRulesDeriver.js.map